<template>
  <div class="search-filters-list">
    <div
      class="search-filters-list__filter-selected"
      :class="{
        'search-filters-list__filter-selected--empty': isCollapsed && refinedItems.length == 0,
        'search-filters-list__filter-selected--active': !isCollapsed,
      }"
      @click="toggleCollapsed"
    >
      <span class="search-filters-list__filter-name">{{ name }}</span>

      <span v-if="isCollapsed" class="adn-icon adn-icon--navigation-direction-down" aria-hidden="true" />
      <span v-else class="adn-icon adn-icon--navigation-direction-up" aria-hidden="true" />
    </div>
    <div v-if="isCollapsed" class="search-filters-list__selected-items">
      <div
        v-for="selectedItem in refinedItems"
        :key="selectedItem.value"
        class="search-filters-list__selected-item"
        :data-test-select-items="selectedItem.value"
        @click="onChange(refine, selectedItem)"
      >
        {{ selectedItem.value }}
        <span class="adn-icon adn-icon--navigation-close" />
      </div>
    </div>
    <adn-input-text
      v-if="showInputSearchFilter"
      v-model="inputTextFilter"
      class="search-filters-search__input"
      icon-left="function-search"
      :placeholder="i18n._('Rechercher...')"
      :icon-right="isInputSearchFilterEmpty ? '' : 'navigation-close'"
      :has-right-click="true"
      data-test-input-search-filter
      @on-icon-right-click="clearInputText"
    ></adn-input-text>
    <div v-if="!isCollapsed" class="search-filters-list__filter-options-list" data-test-filter-list>
      <ul>
        <li v-for="item in filteredItems" :key="item.value" class="search-filters-list__filter-option">
          <div>
            <adn-checkbox
              :checked="item.isRefined"
              :data-test-filter-option="item.value"
              @change="onChange(refine, item)"
            >
              {{ item.value }}
            </adn-checkbox>
          </div>
          <span class="search-filters-list__filter-option-count">{{ item.count.toLocaleString() }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import AdnCheckbox from '@b2ag/design-system/src/components/AdnCheckbox/AdnCheckbox.vue'
import AdnInputText from '@b2ag/design-system/src/components/AdnInputText/AdnInputText.vue'
import { i18n } from '@b2ag/locale'

const itemsThresholdToDisplaySearch = 8

const props = defineProps({
  name: { type: String, required: true },
  isFromSearch: { type: Boolean, required: true },
  items: { type: Array, required: true },
  refine: { type: Function, required: true },
})

const emit = defineEmits(['change'])

const isCollapsed = ref(true)
const inputTextFilter = ref('')

const refinedItems = computed(() => {
  return props.items.filter((item) => item.isRefined)
})

const filteredItems = computed(() => {
  return props.items.filter((item) => item.value.toLowerCase().includes(inputTextFilter.value.toLowerCase()))
})

const showInputSearchFilter = computed(() => {
  return !isCollapsed.value && props.items.length >= itemsThresholdToDisplaySearch
})

const isInputSearchFilterEmpty = computed(() => {
  return inputTextFilter.value === ''
})

const toggleCollapsed = () => {
  isCollapsed.value = !isCollapsed.value
}

const onChange = (refine, item) => {
  refine(item.value)
  emit('change')
}

const clearInputText = () => {
  inputTextFilter.value = ''
}
</script>

<style lang="scss">
.search-filters-list {
  color: var(--color-black);
  position: relative;
}

.search-filters-list__filter-selected {
  display: flex;
  margin-right: 3px;
  padding: 0 0 var(--spacing-3);

  &:hover {
    cursor: pointer;
  }
}

.search-filters-list__filter-selected--active {
  cursor: pointer;
  color: var(--color-text-light);
}

.search-filters-list__filter-selected--empty {
  padding: 0;
}

.search-filters-list__filter-name {
  font-size: var(--font-size-l);
  font-weight: bold;
  color: #3c3c3c;
  flex-grow: 1;
}

.search-filters-list__selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-2);
  font-weight: normal;
  color: var(--color-text-light);
  text-align: right;

  .search-filters-list__selected-item {
    display: flex;
    cursor: pointer;
    border-radius: var(--border-radius-s);
    background-color: var(--color-background);
    padding: var(--spacing-1) var(--spacing-1) var(--spacing-1) var(--spacing-2);
    width: fit-content;
  }
}

.search-filters-list__filter-options-list {
  z-index: 998;
  overflow: hidden scroll;
  top: 0;
  left: 0;
  width: 100%;
  background-color: var(--color-white);
  height: auto;
  max-height: 30rem;
  position: static;
}

.search-filters-list__filter-option {
  padding: var(--spacing-2) var(--spacing-2) var(--spacing-2) 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.search-filters-list__filter-option-count {
  color: var(--color-inactive-dark);
}

.search-filters-search__input {
  margin-bottom: var(--spacing-3);
}
</style>
